import { useState, useRef, useEffect } from "react";
import { ZoomIn, ZoomOut, Maximize, X, Expand, Download, Share2, Loader2 } from "lucide-react";

// Inline images render normally (no scroll hijacking). Clicking opens a
// full-screen lightbox where zoom/pan is safely contained.
export function ZoomableImage({ src, alt = "", className = "", testId }) {
  const [open, setOpen] = useState(false);

  return (
    <>
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); setOpen(true); }}
        aria-label="Enlarge image"
        data-testid={testId}
        className="group relative block w-full h-full cursor-zoom-in overflow-hidden"
      >
        <img src={src} alt={alt} draggable={false} className={className} />
        <span className="absolute bottom-2 right-2 flex h-8 w-8 items-center justify-center rounded-full bg-black/55 text-white opacity-0 group-hover:opacity-100 transition-opacity" aria-hidden="true">
          <Expand className="h-4 w-4" />
        </span>
      </button>
      {open && <Lightbox src={src} alt={alt} onClose={() => setOpen(false)} testId={testId} />}
    </>
  );
}

const clamp = (s) => Math.min(5, Math.max(1, Math.round(s * 100) / 100));
const dist = (a, b) => Math.hypot(a.x - b.x, a.y - b.y);
// Download/share the original upload, not the f_auto (WebP/AVIF) display version.
const originalUrl = (src) => src.replace("/image/upload/f_auto,q_auto/", "/image/upload/");
const fileName = (alt, type) => {
  const base = (alt || "poster").toLowerCase().replace(/['’]/g, "").replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "") || "poster";
  const ext = (type.split("/")[1] || "jpg").replace("jpeg", "jpg").split("+")[0];
  return `${base}.${ext}`;
};
const canShareFiles = () => {
  try { return !!navigator.canShare && navigator.canShare({ files: [new File([""], "x.png", { type: "image/png" })] }); }
  catch { return false; }
};
async function loadFile(src, alt) {
  const res = await fetch(originalUrl(src));
  if (!res.ok) throw new Error("fetch failed");
  const blob = await res.blob();
  const type = blob.type || "image/jpeg";
  return new File([blob], fileName(alt, type), { type });
}

const toolBtn = "h-11 inline-flex items-center justify-center gap-2 rounded-full bg-white/10 text-white hover:bg-white/25 transition-colors disabled:opacity-60";

export function Lightbox({ src, alt, onClose, testId, children, onNavigate }) {
  const [scale, setScale] = useState(1);
  const [pos, setPos] = useState({ x: 0, y: 0 });
  const [swipeDx, setSwipeDx] = useState(0);
  const [busy, setBusy] = useState(false);
  const [shareable] = useState(canShareFiles);
  const pointers = useRef(new Map());
  const gesture = useRef(null);
  const fileRef = useRef(null);
  const imgRef = useRef(null);

  const reset = () => { setScale(1); setPos({ x: 0, y: 0 }); };
  const zoomBy = (d) => setScale((s) => { const ns = clamp(s + d); if (ns <= 1) setPos({ x: 0, y: 0 }); return ns; });

  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden"; // lock page scroll while open
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => { document.body.style.overflow = prev; window.removeEventListener("keydown", onKey); };
  }, [onClose]);

  useEffect(() => {
    const el = imgRef.current;
    if (!el) return;
    const onWheel = (e) => { e.preventDefault(); zoomBy(e.deltaY < 0 ? 0.3 : -0.3); };
    el.addEventListener("wheel", onWheel, { passive: false });
    return () => el.removeEventListener("wheel", onWheel);
  }, []);

  // Pre-load the file on share-capable devices so the share sheet opens inside the tap gesture.
  useEffect(() => {
    if (!shareable) return;
    let alive = true;
    fileRef.current = null;
    loadFile(src, alt).then((f) => { if (alive) fileRef.current = f; }).catch(() => {});
    return () => { alive = false; };
  }, [src, alt, shareable]);

  const download = async () => {
    setBusy(true);
    try {
      const file = fileRef.current || (await loadFile(src, alt));
      const url = URL.createObjectURL(file);
      const a = document.createElement("a");
      a.href = url;
      a.download = file.name;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 4000);
    } catch {
      window.open(originalUrl(src), "_blank", "noopener");
    } finally {
      setBusy(false);
    }
  };

  const share = async () => {
    const file = fileRef.current;
    try {
      if (file && navigator.canShare({ files: [file] })) await navigator.share({ files: [file], title: alt });
      else await navigator.share({ title: alt, url: originalUrl(src) });
    } catch (e) {
      if (e?.name !== "AbortError") download();
    }
  };

  // One pointer: pan when zoomed, otherwise swipe to the prev/next photo. Two pointers: pinch-zoom.
  const onPointerDown = (e) => {
    try { e.currentTarget.setPointerCapture(e.pointerId); } catch { /* pointer already gone */ }
    pointers.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
    const pts = [...pointers.current.values()];
    if (pts.length === 1) {
      gesture.current = { sx: e.clientX, sy: e.clientY, noSwipe: false, pinch: null, pan: scale > 1 ? { x: e.clientX - pos.x, y: e.clientY - pos.y } : null };
    } else if (pts.length === 2 && gesture.current) {
      Object.assign(gesture.current, { pinch: { d: dist(pts[0], pts[1]) || 1, s: scale }, noSwipe: true, pan: null });
      setSwipeDx(0);
    }
  };
  const onPointerMove = (e) => {
    if (!pointers.current.has(e.pointerId)) return;
    pointers.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
    const g = gesture.current;
    if (!g) return;
    const pts = [...pointers.current.values()];
    if (pts.length === 2 && g.pinch) {
      const ns = clamp((g.pinch.s * dist(pts[0], pts[1])) / g.pinch.d);
      setScale(ns);
      if (ns <= 1) setPos({ x: 0, y: 0 });
    } else if (pts.length === 1 && g.pan) {
      setPos({ x: e.clientX - g.pan.x, y: e.clientY - g.pan.y });
    } else if (pts.length === 1 && onNavigate && !g.noSwipe && scale <= 1) {
      setSwipeDx(e.clientX - g.sx);
    }
  };
  const onPointerUp = (e) => {
    pointers.current.delete(e.pointerId);
    const g = gesture.current;
    if (!g) return;
    if (pointers.current.size === 1) {
      const [p] = [...pointers.current.values()];
      g.pinch = null;
      g.pan = scale > 1 ? { x: p.x - pos.x, y: p.y - pos.y } : null;
      return;
    }
    if (pointers.current.size === 0) {
      const dx = e.clientX - g.sx;
      const dy = e.clientY - g.sy;
      gesture.current = null;
      setSwipeDx(0);
      if (onNavigate && !g.noSwipe && scale <= 1 && Math.abs(dx) > 50 && Math.abs(dx) > Math.abs(dy) * 1.5) onNavigate(dx < 0 ? 1 : -1);
    }
  };
  const onPointerCancel = (e) => {
    if (gesture.current) gesture.current.noSwipe = true;
    onPointerUp(e);
  };
  const onDoubleClick = () => (scale > 1 ? reset() : setScale(2.5));

  return (
    <div
      className={`fixed inset-0 z-[100] bg-black/90 backdrop-blur-sm flex flex-col items-center justify-center ${children ? "pb-20" : ""}`}
      onClick={onClose}
      data-testid={testId ? `${testId}-lightbox` : "image-lightbox"}
      role="dialog"
      aria-modal="true"
      aria-label={alt || "Image viewer"}
    >
      <div className="absolute top-4 right-4 z-10 flex items-center gap-2" onClick={(e) => e.stopPropagation()} data-testid="lightbox-toolbar">
        {shareable && (
          <button onClick={share} aria-label="Share image" data-testid="lightbox-share-btn" className={`${toolBtn} px-4 text-sm font-semibold`}>
            <Share2 className="h-5 w-5" /> <span className="hidden sm:inline">Share</span>
          </button>
        )}
        <button onClick={download} disabled={busy} aria-label="Download image" data-testid="lightbox-download-btn" className={`${toolBtn} px-4 text-sm font-semibold`}>
          {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : <Download className="h-5 w-5" />} <span className="hidden sm:inline">Download</span>
        </button>
        <button onClick={onClose} aria-label="Close" data-testid="lightbox-close-btn" className={`${toolBtn} w-11`}>
          <X className="h-6 w-6" />
        </button>
      </div>

      <div className={`max-w-[92vw] ${children ? "max-h-[64vh]" : "max-h-[88vh]"} overflow-hidden`} onClick={(e) => e.stopPropagation()}>
        <img
          ref={imgRef}
          src={src}
          alt={alt}
          onDoubleClick={onDoubleClick}
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={onPointerUp}
          onPointerCancel={onPointerCancel}
          draggable={false}
          data-testid={testId ? `${testId}-lightbox-img` : "lightbox-img"}
          style={{
            transform: `translate(${pos.x + swipeDx}px, ${pos.y}px) scale(${scale})`,
            cursor: scale > 1 ? "grab" : "zoom-in",
            transition: gesture.current ? "none" : "transform 0.15s ease-out",
            touchAction: "none",
          }}
          className={`max-w-[92vw] ${children ? "max-h-[64vh]" : "max-h-[88vh]"} object-contain select-none`}
        />
      </div>
      {children}

      <div
        className="absolute bottom-5 left-1/2 -translate-x-1/2 flex items-center gap-1 bg-black/65 backdrop-blur-sm rounded-full px-1.5 py-1 select-none"
        onClick={(e) => e.stopPropagation()}
        data-testid={testId ? `${testId}-controls` : "zoom-controls"}
      >
        <button onClick={() => zoomBy(-0.5)} disabled={scale <= 1} aria-label="Zoom out" data-testid="zoom-out-btn" className="h-9 w-9 flex items-center justify-center rounded-full text-white hover:bg-white/20 disabled:opacity-40 transition-colors"><ZoomOut className="h-4 w-4" /></button>
        <span className="text-white text-xs font-semibold w-11 text-center tabular-nums" data-testid="zoom-level">{Math.round(scale * 100)}%</span>
        <button onClick={() => zoomBy(0.5)} disabled={scale >= 5} aria-label="Zoom in" data-testid="zoom-in-btn" className="h-9 w-9 flex items-center justify-center rounded-full text-white hover:bg-white/20 disabled:opacity-40 transition-colors"><ZoomIn className="h-4 w-4" /></button>
        <button onClick={reset} disabled={scale <= 1} aria-label="Reset zoom" data-testid="zoom-reset-btn" className="h-9 w-9 flex items-center justify-center rounded-full text-white hover:bg-white/20 disabled:opacity-40 transition-colors"><Maximize className="h-4 w-4" /></button>
      </div>
    </div>
  );
}
